import { useEffect, useRef } from "react";
import { hasAuthParams, useAuth } from "react-oidc-context";
import { Routes, Route, Navigate } from "react-router-dom";
import { config } from "./lib/config";
import { isAccessDenied } from "./lib/auth";
import { BootScreen, AccessDeniedScreen, ErrorScreen } from "./components/screens";
import { Shell } from "./components/Shell";
import { DeployDemoProjectPage } from "./pages/DeployDemoProject";
import { SampleProjectMasterPage } from "./pages/SampleProjectMaster";
import { AdminUsersPage } from "./pages/AdminUsers";
import { SupportUsersPage } from "./pages/SupportUsers";
import { OrganizationsPage } from "./pages/Organizations";
import { CreateOrganizationPage } from "./pages/CreateOrganization";
import { DeletedDocumentsPage } from "./pages/DeletedDocuments";
import { OrganizationInvitationsPage } from "./pages/OrganizationInvitations";
import { GuestUsersPage } from "./pages/GuestUsers";
import { BrokenUsersPage } from "./pages/BrokenUsers";
import { ObservabilityServicesPage } from "./pages/ObservabilityServices";
import { CostAllocationPage } from "./pages/CostAllocation";
import { ScheduledOutagesPage } from "./pages/ScheduledOutages";

export function App() {
  const auth = useAuth();
  // One silent (refresh-token) attempt per page load, so a failed refresh falls through to the redirect once.
  const triedSilentRenew = useRef(false);

  // Kick off the sign-in automatically — there's no anonymous view of this portal.
  useEffect(() => {
    if (
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading &&
      !auth.error
    ) {
      // A stored session whose access token has expired (laptop sleep, reopened tab) but which still holds a
      // refresh token is renewed at /connect/token, not sent back through the interactive sign-in pages
      // (which may be behind an outage placeholder). There is no silent_redirect_uri, so this can never
      // fall back to an iframe. Only if the refresh fails do we start the interactive sign-in.
      if (auth.user?.refresh_token && !triedSilentRenew.current) {
        triedSilentRenew.current = true;
        void auth.signinSilent().then((user) => {
          if (!user) void auth.signinRedirect();
        });
        return;
      }
      void auth.signinRedirect();
    }
  }, [auth.isAuthenticated, auth.activeNavigator, auth.isLoading, auth.error, auth]);

  if (auth.activeNavigator === "signinRedirect" || auth.activeNavigator === "signinSilent") {
    return <BootScreen line="Redirecting to identity server…" status="HANDSHAKE" />;
  }
  if (auth.activeNavigator === "signoutRedirect") {
    return <BootScreen line="Signing out…" status="TEARDOWN" />;
  }
  if (auth.isLoading) {
    return <BootScreen line="Establishing session…" status="BOOT" />;
  }

  if (auth.error) {
    if (isAccessDenied(auth.error)) {
      return <AccessDeniedScreen onSignOut={() => void auth.signoutRedirect()} />;
    }
    return (
      <ErrorScreen
        message={auth.error.message}
        onRetry={() => void auth.signinRedirect()}
        onSignOut={() => void auth.removeUser()}
      />
    );
  }

  if (!auth.isAuthenticated || !auth.user) {
    return <BootScreen line="Awaiting authentication…" status="IDLE" />;
  }

  // Belt-and-braces: a token without the admin scope means the identity server
  // didn't grant it — treat that exactly like access-denied.
  if (!auth.user.scopes.includes(config.adminScope)) {
    return <AccessDeniedScreen onSignOut={() => void auth.signoutRedirect()} />;
  }

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<DeployDemoProjectPage />} />
        <Route path="/sample-project-master" element={<SampleProjectMasterPage />} />
        <Route path="/outages" element={<ScheduledOutagesPage />} />
        <Route path="/admins" element={<AdminUsersPage />} />
        <Route path="/support/users" element={<SupportUsersPage />} />
        <Route path="/support/guest-users" element={<GuestUsersPage />} />
        <Route path="/support/organizations" element={<OrganizationsPage />} />
        <Route path="/support/create-organization" element={<CreateOrganizationPage />} />
        <Route path="/support/deleted-documents" element={<DeletedDocumentsPage />} />
        <Route path="/support/organization-invitations" element={<OrganizationInvitationsPage />} />
        <Route path="/support/broken-users" element={<BrokenUsersPage />} />
        <Route path="/observability/services" element={<ObservabilityServicesPage />} />
        <Route path="/cost/allocation" element={<CostAllocationPage />} />
        {/* /callback is consumed by oidc-client-ts during load; just bounce home. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}
